import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    Stack,
    CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import { ReactNotifications } from "react-notifications-component";

import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { dataGridStyle, palette } from "../../../theme";
import { MUTED, SURFACE, SURFACE_HEAD, BORDER } from "../../dashboard/dashboard-components/resellerUi";
import Header from "../../../components/Header";
import WarningDialog from "../../../components/WarningDialog";
import API from "../../../api";

import {
    startToast,
    updateToastError,
    updateToastSuccess,
} from "../../../utils/notification-loading";

dayjs.extend(utc);
dayjs.extend(timezone);

// ISO dal server -> "DD/MM/YYYY HH:mm" nel timezone dell'utente; null se non valida.
const formatDate = (v) => {
    if (!v || /NaN/.test(String(v))) return null;
    const d = dayjs(v);
    if (!d.isValid()) return null;
    const tz = sessionStorage.getItem("timezone") || "Europe/Rome";
    return d.tz(tz).format("DD/MM/YYYY HH:mm");
};

// ─── Step status icon ─────────────────────────────────────────────────────────

const StepIcon = ({ status }) => {
    if (status === "loading") {
        return (
            <CircularProgress
                size={18}
                sx={{ color: "#449095" }}
            />
        );
    }

    if (status === "success") {
        return (
            <CheckCircleIcon
                sx={{
                    fontSize: 20,
                    color: "#27ae60",
                }}
            />
        );
    }

    if (status === "error") {
        return (
            <ErrorIcon
                sx={{
                    fontSize: 20,
                    color: "#c0392b",
                }}
            />
        );
    }

    return (
        <RadioButtonUncheckedIcon
            sx={{
                fontSize: 20,
                color: "#444",
            }}
        />
    );
};

// ─── Deletion steps ───────────────────────────────────────────────────────────

const STEPS = [
    {
        key: "cloud",
        label: "Cloud data deletion",
        desc: "Permanently deletes all security events, network anomalies and collected data from the cloud.",
    },
    {
        key: "lecs",
        label: "LECS reset",
        desc: "Resets the AI engine and the asset counter on the device.",
    },
];

// ─── Deletion step row ────────────────────────────────────────────────────────

const StepRow = ({
    step,
    status,
    errorMsg,
    onClick,
}) => {
    const idle = status === "idle";

    return (
        <Box
            onClick={idle ? onClick : undefined}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor:
                    status === "success"
                        ? "rgba(39,174,96,0.25)"
                        : status === "error"
                            ? "rgba(192,57,43,0.25)"
                            : status === "loading"
                                ? "rgba(69,144,149,0.25)"
                                : "#2a2a2a",
                backgroundColor:
                    status === "success"
                        ? "rgba(39,174,96,0.05)"
                        : status === "error"
                            ? "rgba(192,57,43,0.05)"
                            : status === "loading"
                                ? "rgba(69,144,149,0.05)"
                                : "rgba(255,255,255,0.02)",
                cursor: idle ? "pointer" : "default",
                transition: "all 0.2s ease",
                ...(idle && {
                    "&:hover": {
                        borderColor: "rgba(192,57,43,0.5)",
                        backgroundColor: "rgba(192,57,43,0.06)",
                    },
                }),
            }}
        >
            <Box
                sx={{
                    mt: 0.15,
                    flexShrink: 0,
                }}
            >
                <StepIcon status={status} />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                    }}
                >
                    {step.label}
                </Typography>

                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        color: "#666",
                        mt: 0.25,
                    }}
                >
                    {step.desc}
                </Typography>

                {idle && (
                    <Typography
                        sx={{
                            fontSize: "0.70rem",
                            color: "rgba(192,57,43,0.7)",
                            mt: 0.4,
                            fontStyle: "italic",
                        }}
                    >
                        Click to execute
                    </Typography>
                )}

                {status === "error" && errorMsg && (
                    <Typography
                        sx={{
                            fontSize: "0.72rem",
                            color: "#c0392b",
                            mt: 0.5,
                            fontWeight: 600,
                        }}
                    >
                        {errorMsg}
                    </Typography>
                )}

                {status === "success" && (
                    <Typography
                        sx={{
                            fontSize: "0.72rem",
                            color: "#27ae60",
                            mt: 0.5,
                            fontWeight: 600,
                        }}
                    >
                        Done
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const NFR = ({ setCustomerName }) => {
    const navigate = useNavigate();

    const [nfr, setNfr] = useState([]);
    const [manageOpen, setManageOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);
    const [charged, setCharged] = useState(false);

    // ─── WarningDialog state ──────────────────────────────────────────────────

    const [warningOpen, setWarningOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // ─── Deletion progress dialog state ───────────────────────────────────────

    const [deleteOpen, setDeleteOpen] = useState(false);

    const [cloudStatus, setCloudStatus] = useState("idle");
    const [lecsStatus, setLecsStatus] = useState("idle");

    const [cloudError, setCloudError] = useState("");
    const [lecsError, setLecsError] = useState("");

    const [cloudRunning, setCloudRunning] = useState(false);
    const [lecsRunning, setLecsRunning] = useState(false);

    const cloudDone =
        cloudStatus === "success" ||
        cloudStatus === "error";

    const lecsDone =
        lecsStatus === "success" ||
        lecsStatus === "error";

    const allDone = cloudDone && lecsDone;

    // ─── Load NFR data ────────────────────────────────────────────────────────

    useEffect(() => {
        API.getNFR({})
            .then((result) => {
                const transformedData = result.data.map((item) => ({
                    id: item.matricola,
                    ...item,
                }));

                setNfr(transformedData);
                setCharged(true);
            })
            .catch((error) => {
                console.error(
                    "[NFR] An error occurred while retrieving data:",
                    error
                );

                setCharged(true);
            });
    }, []);

    // ─── Manage popover ───────────────────────────────────────────────────────

    const handleClick = (row) => {
        setManageOpen(true);
        setSelectedRow(row);

        API.setCurrentCustomer({
            serial_number: row.matricola,
        })
            .then((response) => {
                if (response.status === 200) {
                    window.sessionStorage.setItem(
                        "currentCustomer",
                        response.data.currentCustomer
                    );

                    window.sessionStorage.setItem(
                        "customerName",
                        row.ragioneSociale
                    );

                    window.sessionStorage.setItem(
                        "tipoLecs",
                        response.data.tipoLecs
                    );
                } else {
                    startToast();
                    updateToastError("Failed to set current customer");
                }
            })
            .catch((error) => {
                console.error("[handleClick] error:", error);
            });
    };

    const handleClose = () => {
        setManageOpen(false);
        setSelectedRow(null);
    };

    // ─── Open customer dashboard ──────────────────────────────────────────────

    const handleSelect = (
        serialNumber,
        ragioneSociale
    ) => {
        startToast();

        API.postCurrentCustomer({
            serial_number: serialNumber,
        })
            .then((response) => {
                setCustomerName(ragioneSociale);

                updateToastSuccess("OK");

                window.sessionStorage.setItem(
                    "tipoLecs",
                    response.data.tipoLecs
                );

                navigate("/reseller/myC/dashboard");
            })
            .catch(() => {
                updateToastError(
                    `You are not authorized to view user's LECS ${ragioneSociale}. Please request permission to continue.`
                );
            });
    };

    // ─── WarningDialog ────────────────────────────────────────────────────────

    const handleOpenDeleteWarning = () => {
        setConfirmDelete(false);
        setWarningOpen(true);
    };

    // ─── Progress dialog ──────────────────────────────────────────────────────

    const handleOpenDelete = () => {
        setCloudStatus("idle");
        setLecsStatus("idle");

        setCloudError("");
        setLecsError("");

        setCloudRunning(false);
        setLecsRunning(false);

        setDeleteOpen(true);
    };

    const handleDeleteDialogClose = () => {
        if (cloudRunning || lecsRunning) {
            return;
        }

        setDeleteOpen(false);

        if (
            cloudStatus === "success" &&
            lecsStatus === "success"
        ) {
            handleClose();
        }
    };

    // ─── Cloud data deletion API ──────────────────────────────────────────────

    const handleCloudDelete = () => {
        setCloudRunning(true);
        setCloudStatus("loading");
        setCloudError("");

        API.putCustomerDel({})
            .then(() => {
                setCloudStatus("success");
            })
            .catch((error) => {
                const message =
                    error?.response?.data?.error ||
                    error?.response?.data?.message ||
                    "Cloud data deletion failed.";

                setCloudError(message);
                setCloudStatus("error");
            })
            .finally(() => {
                setCloudRunning(false);
            });
    };

    // ─── LECS reset API ───────────────────────────────────────────────────────

    const handleLecsReset = () => {
        setLecsRunning(true);
        setLecsStatus("loading");
        setLecsError("");

        API.resetLecsNFR()
            .then(() => {
                setLecsStatus("success");
            })
            .catch((error) => {
                const data = error?.response?.data ?? {};
                const results = data?.data?.results ?? {};

                const failed = Object.entries(results)
                    .filter(
                        ([, value]) =>
                            value !== "ok" &&
                            value !== "not_available"
                    )
                    .map(
                        ([key, value]) =>
                            `${key}: ${value}`
                    )
                    .join(", ");

                const message =
                    failed ||
                    data?.detail ||
                    data?.message ||
                    data?.error ||
                    `HTTP ${error?.response?.status ?? "?"}`;

                setLecsError(message);
                setLecsStatus("error");
            })
            .finally(() => {
                setLecsRunning(false);
            });
    };

    // ─── Execute deletion only after WarningDialog confirmation ───────────────

    useEffect(() => {
        if (!confirmDelete) {
            return;
        }

        setConfirmDelete(false);
        setWarningOpen(false);

        // Shows the operation progress dialog.
        handleOpenDelete();

        // These APIs are executed only after confirmation.
        handleCloudDelete();
        handleLecsReset();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmDelete]);

    // ─── Rows: sempre ordinate per nome, filtrate dalla ricerca ───────────────

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const nameOf = (r) => (r.ragioneSociale || "").trim();

        return [...nfr]
            .sort((x, y) =>
                nameOf(x).localeCompare(nameOf(y), undefined, {
                    sensitivity: "base",
                    numeric: true,
                })
            )
            .filter(
                (r) =>
                    !q ||
                    [r.ragioneSociale, r.email, r.matricola, r.telefono].some(
                        (v) => String(v ?? "").toLowerCase().includes(q)
                    )
            );
    }, [nfr, search]);

    // ─── DataGrid columns ─────────────────────────────────────────────────────

    const muted = (text) => (
        <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
            {text}
        </Typography>
    );

    const columns = [
        {
            field: "ragioneSociale",
            headerName: "Name",
            minWidth: 180,
            flex: 1,
            renderCell: (p) => p.value || muted("-"),
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 240,
            flex: 1,
            renderCell: (p) => p.value || muted("-"),
        },
        {
            field: "telefono",
            headerName: "Phone",
            width: 140,
            renderCell: (p) => p.value || muted("-"),
        },
        {
            field: "matricola",
            headerName: "Serial number",
            width: 130,
        },
        {
            field: "giorniRimanenti",
            headerName: "Days left",
            width: 100,
            renderCell: (p) => {
                const n = Number(p.value);
                if (p.value === null || p.value === undefined || !Number.isFinite(n)) {
                    return muted("-");
                }
                const color =
                    n <= 0 ? palette.error.light : n <= 30 ? "#EF9F27" : undefined;
                return (
                    <Typography component="span" sx={{ fontSize: 13, color }}>
                        {p.value}
                    </Typography>
                );
            },
        },
        {
            field: "dataAttivazione",
            headerName: "Activation date",
            width: 160,
            renderCell: (p) => formatDate(p.value) ?? muted("-"),
        },
        {
            field: "actions",
            headerName: "",
            width: 230,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "right",
            renderCell: (p) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                            handleSelect(p.row.matricola, p.row.ragioneSociale)
                        }
                    >
                        Dashboard
                    </Button>
                    <Tooltip title="Manage / delete data" arrow>
                        <IconButton
                            size="small"
                            aria-label="Manage"
                            onClick={() => handleClick(p.row)}
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "8px",
                                border: "1px solid rgba(255,255,255,0.14)",
                                "&:hover": {
                                    borderColor: palette.primary.light,
                                    backgroundColor: "rgba(69,144,149,0.12)",
                                },
                            }}
                        >
                            <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // ─── Manage dialog (uno solo, fuori dalla griglia) ────────────────────────

    const detail = (label, value) => (
        <Box>
            <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, wordBreak: "break-all" }}>
                {value || "-"}
            </Typography>
        </Box>
    );

    const manageDialog = (
        <Dialog
            open={manageOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: SURFACE,
                        backgroundImage: "none",
                        border: `1px solid ${BORDER}`,
                        borderRadius: "10px",
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.25,
                    backgroundColor: SURFACE_HEAD,
                    fontSize: 16,
                    fontWeight: 700,
                }}
            >
                <Box component="span">
                    <Box component="span" sx={{ color: MUTED, fontWeight: 500 }}>
                        Manage{" "}
                    </Box>
                    {selectedRow?.ragioneSociale}
                </Box>
                <IconButton size="small" onClick={handleClose} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "20px !important" }}>
                <Stack spacing={2}>
                    {detail("Serial number", selectedRow?.matricola)}
                    {detail("Email", selectedRow?.email)}
                    {detail("License key", selectedRow?.codiceLicenza)}

                    <Divider />

                    <Box
                        sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: "1px solid rgba(192,57,43,0.35)",
                            backgroundColor: "rgba(192,57,43,0.06)",
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                            Danger zone
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.5 }}>
                            Deletes all cloud data of this LECS and resets the
                            device. This action is irreversible.
                        </Typography>
                        <Button
                            variant="contained"
                            color="error"
                            startIcon={<DeleteForeverIcon />}
                            onClick={handleOpenDeleteWarning}
                        >
                            Delete all data
                        </Button>
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );

    // ─── Deletion progress dialog ─────────────────────────────────────────────

    const deleteDialog = (
        <Dialog
            open={deleteOpen}
            onClose={handleDeleteDialogClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: SURFACE,
                        backgroundImage: "none",
                        border: "1px solid rgba(192,57,43,0.3)",
                        borderRadius: "10px",
                    },
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                >
                    <DeleteForeverIcon
                        sx={{
                            color: "#c0392b",
                            fontSize: 22,
                        }}
                    />

                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "1rem",
                        }}
                    >
                        Delete data — {selectedRow?.ragioneSociale}
                    </Typography>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={1.25}>
                    <StepRow
                        step={STEPS[0]}
                        status={cloudStatus}
                        errorMsg={cloudError}
                        onClick={handleCloudDelete}
                    />

                    <StepRow
                        step={STEPS[1]}
                        status={lecsStatus}
                        errorMsg={lecsError}
                        onClick={handleLecsReset}
                    />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1.25}
                >
                    {!allDone ? (
                        <Button
                            variant="outlined"
                            onClick={handleDeleteDialogClose}
                            disabled={
                                cloudRunning ||
                                lecsRunning
                            }
                            sx={{
                                borderColor: "#555",
                                color: "#888",
                            }}
                        >
                            Cancel
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={handleDeleteDialogClose}
                            sx={{
                                borderColor: "#449095",
                                color: "#449095",
                            }}
                        >
                            Close
                        </Button>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );

    return (
        <Box m="20px">
            <Box mb={2}>
                <Header
                    title="NFR"
                    subtitle=""
                />
            </Box>

            <ReactNotifications />

            <WarningDialog
                open={warningOpen}
                setOpen={setWarningOpen}
                setCheck={setConfirmDelete}
                message="Are you sure you want to delete all data associated with this LECS? This action is irreversible."
            />

            {manageDialog}
            {deleteDialog}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    mb: 2,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search by name, email or serial…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Chip
                    size="small"
                    label={`${rows.length}${rows.length !== nfr.length ? ` / ${nfr.length}` : ""} NFR`}
                    sx={{
                        color: MUTED,
                        backgroundColor: "transparent",
                        border: "1px solid rgba(255,255,255,0.14)",
                    }}
                />
            </Box>

            <Box height="calc(100vh - 250px)" minHeight={400}>
                <DataGrid
                    sx={dataGridStyle}
                    loading={!charged}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    localeText={{ noRowsLabel: "No NFR found" }}
                />
            </Box>
        </Box>
    );
};

export default NFR;